import { alertVariants } from "@visiblethread/react-components";
import localhost from "apis/localhost";
import { changePasswordConfirmationModal } from "components/Modal/confModalTypes";
import { MULTIPART_FORM_DATA_CONTENT_TYPE } from "constants/app";
import {
  ADMIN,
  CLOUD_USAGE,
  FEATURE_FLAGS,
  LOGIN,
  STATUS,
  SYSTEM_ADMIN,
  USER_ADMIN,
} from "constants/routes";
import { modalTypes } from "constants/types";
import { TAB_ID, WORKSPACE_SYNC_CHANNEL } from "hooks/generic/useWorkspaceSync";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  currentSessionInfo,
  pageLoading,
  setIsInitialized,
  setIsSwitchingWorkspace,
  showGlobalAlert,
} from "slices/app/appSlice";
import { hasRole, LS_SIDEMENU_GROUPS_CACHE } from "utils";
import { resolveEntityLink } from "utils/entityLinks";
import {
  capturePendingRedirect,
  PENDING_REDIRECT_KEY,
  PENDING_WORKSPACE_ALERT_KEY,
} from "utils/pendingRedirect";
import { setExtraSentryInfo } from "utils/sentry";

import { selectCsrf, selectUsername } from "../selectors";

export { PENDING_REDIRECT_KEY };

export const setWorkspace = (workspaceId) => async (dispatch) => {
  dispatch(setIsSwitchingWorkspace(true));
  dispatch(pageLoading(true));

  const formData = new FormData();
  formData.append("workspaceId", workspaceId);

  await localhost
    .post("/setWorkspace", formData)
    .then(async (response) => {
      const { data } = response;
      const channel = new BroadcastChannel(WORKSPACE_SYNC_CHANNEL);
      channel.postMessage({ type: "workspace-changed", tabId: TAB_ID });
      channel.close();
      window.location.href = data;
    })
    .catch((error) => {
      localStorage.removeItem(PENDING_REDIRECT_KEY);
      localStorage.removeItem(PENDING_WORKSPACE_ALERT_KEY);
      if (error.status === 401 || error.status === 403) {
        window.location.href = "/#/login/timed-out";
      } else if (error.status === 901) {
        window.location.href = "/#/login/permissions-updated";
      } else if (error.status === 902) {
        window.location.href = "/#/login/logged-out";
      }
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(setIsSwitchingWorkspace(false));
      dispatch(pageLoading(false));
    });
};

export const changePassword =
  (oldPassword, newPassword) => async (dispatch) => {
    dispatch(pageLoading(true));
    await localhost
      .post(
        "/changePassword",
        {
          oldPassword,
          newPassword,
        },
        {
          headers: {
            "Content-Type": MULTIPART_FORM_DATA_CONTENT_TYPE,
          },
        }
      )
      .then(async () => {
        dispatch(
          showModal(
            modalTypes.CONFIRMATION,
            changePasswordConfirmationModal(dispatch)
          )
        );
        dispatch(hideModal(modalTypes.CHANGE_PASSWORD));
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.CHANGE_PASSWORD));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const changePasswordDirect =
  (handleSuccess, handleFailure, oldPassword, newPassword) =>
  async (dispatch) => {
    dispatch(pageLoading(true));
    await localhost
      .post(
        "/changePassword",
        {
          oldPassword,
          newPassword,
        },
        {
          headers: {
            "Content-Type": MULTIPART_FORM_DATA_CONTENT_TYPE,
          },
        }
      )
      .then(handleSuccess)
      .catch((e) => {
        handleFailure(e);
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const forgotPassword =
  (handleSuccess, handleFailure, usermail) => async (dispatch) => {
    dispatch(pageLoading(true));
    await localhost
      .post(
        "/password/generate",
        {
          usermail,
        },
        {
          headers: {
            "Content-Type": MULTIPART_FORM_DATA_CONTENT_TYPE,
          },
        }
      )
      .then(handleSuccess)
      .catch(() => {
        handleFailure();
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const resetPassword =
  (handleSuccess, handleFailure, token, userNewPassword) =>
  async (dispatch) => {
    dispatch(pageLoading(true));
    await localhost
      .post(
        "/password/reset",
        {
          token,
          userNewPassword,
        },
        {
          headers: {
            "Content-Type": MULTIPART_FORM_DATA_CONTENT_TYPE,
          },
        }
      )
      .then(handleSuccess)
      .catch(() => {
        handleFailure();
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const handleSessionInfoNotLoggedIn = (navigate, data) => {
  if (data?.userInfo?.authType === "SAML") {
    const isOnPremSsoAdminLogin =
      !data?.userInfo?.cloudMode &&
      window.location.hash.includes("/systemAdminLogin");
    if (!isOnPremSsoAdminLogin) {
      capturePendingRedirect();
      window.location = "/saml/login";
    }
    return;
  }
  if (
    !window.location.hash.includes("/user/password/expired") &&
    !window.location.hash.includes("/user/password/reset")
  ) {
    capturePendingRedirect();
    window.history.replaceState({}, "", window.location.pathname);
    navigate(LOGIN);
  }
};

// A real internal Customer Success account holds this role and nothing else, so it lands on the
// Cloud Usage tool. Staff who also hold ordinary roles keep the normal app as their landing page.
const isCustomerSuccessOnly = (roles) => {
  const roleList = (roles || "").split(",").filter(Boolean);
  return roleList.length === 1 && roleList[0] === "customer_success";
};

// Landing only applies when the session resolves somewhere outside Cloud Usage. A refresh on a
// deep link such as #/cloud-usage/workspaces/7 must keep the workspace the user was looking at.
const isAlreadyInCloudUsage = () => window.location.hash.includes(CLOUD_USAGE);

const getUnlicensedRedirectPath = (data) => {
  if (hasRole(data?.userInfo?.roles, "system_admin")) {
    return SYSTEM_ADMIN;
  }
  if (!hasRole(data?.userInfo?.roles, "user_admin")) {
    return "/login";
  }
  return data?.userInfo?.cloudMode ? ADMIN : USER_ADMIN;
};

const consumePendingRedirect = (dispatch, navigate) => {
  const pendingRedirect = localStorage.getItem(PENDING_REDIRECT_KEY);
  if (!pendingRedirect) {
    return window.location.hash;
  }

  localStorage.removeItem(PENDING_REDIRECT_KEY);
  const pendingWorkspaceName = localStorage.getItem(
    PENDING_WORKSPACE_ALERT_KEY
  );
  if (pendingWorkspaceName) {
    localStorage.removeItem(PENDING_WORKSPACE_ALERT_KEY);
    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: `You're now in workspace ${pendingWorkspaceName}`,
      })
    );
  }
  navigate(pendingRedirect.replace(/^#/, ""));
  return pendingRedirect;
};

export const handleSessionInfoLoggedIn = (
  dispatch,
  navigate,
  data,
  { skipPendingRedirect = false } = {}
) => {
  if (
    data?.userInfo?.workspaceStatus === "Running" ||
    data?.userInfo?.workspaceStatus === "Setup"
  ) {
    navigate(STATUS);
    return;
  }
  if (isCustomerSuccessOnly(data?.userInfo?.roles)) {
    if (!isAlreadyInCloudUsage()) {
      navigate(CLOUD_USAGE);
    }
    return;
  }
  if (
    hasRole(data?.userInfo?.roles, "feature_flag") &&
    !hasRole(data?.userInfo?.roles, "user")
  ) {
    navigate(FEATURE_FLAGS);
    return;
  }
  if (
    !window.location.hash.includes("/setup") &&
    !window.location.hash.includes("/login") &&
    data?.application?.licensed === false
  ) {
    navigate(getUnlicensedRedirectPath(data));
    return;
  }

  if (skipPendingRedirect) {
    return window.location.hash;
  }

  return consumePendingRedirect(dispatch, navigate);
};

export const ENTITY_LINK_RESOLUTION = {
  RESOLVED: "resolved",
  SWITCHING: "switching",
};

export const switchWorkspaceForEntityLink =
  (targetHash, currentWorkspaceId) => async (dispatch) => {
    const entityLink = resolveEntityLink(targetHash);
    if (!entityLink) {
      return ENTITY_LINK_RESOLUTION.RESOLVED;
    }

    const { entityId, entityType } = entityLink;
    const params = { entityId, entityType };
    try {
      const { data } = await localhost.get("/nextgen/entity-workspace", {
        params,
      });

      if (!data?.workspaceId || data.workspaceId === currentWorkspaceId) {
        return ENTITY_LINK_RESOLUTION.RESOLVED;
      }

      localStorage.setItem(PENDING_REDIRECT_KEY, targetHash);
      localStorage.setItem(PENDING_WORKSPACE_ALERT_KEY, data.workspaceName);
      dispatch(setWorkspace(data.workspaceId));
      return ENTITY_LINK_RESOLUTION.SWITCHING;
    } catch {
      // Not accessible in any of the recipient's own workspaces — the page's existing
      // not-found handling takes over once it loads; there's nothing to switch to.
      return ENTITY_LINK_RESOLUTION.RESOLVED;
    }
  };

export const handleSessionInfo = (
  dispatch,
  navigate,
  data,
  { skipPendingRedirect = false } = {}
) => {
  if (window.location.hash.includes("/setup")) {
    navigate("/setup");
    return;
  }
  if (!data?.userInfo?.isLoggedIn) {
    handleSessionInfoNotLoggedIn(navigate, data);
  } else {
    handleSessionInfoLoggedIn(dispatch, navigate, data, {
      skipPendingRedirect,
    });
  }

  // Pass the session's user info along to Sentry...
  setExtraSentryInfo(data);
  dispatch(currentSessionInfo(data));

  if (data?.aupAcceptanceRequired && data?.userInfo?.workspaceName !== null) {
    dispatch(showModal(modalTypes.AUP_ACCEPTANCE));
  }
};

export const fetchSessionInfo = () => async (dispatch) => {
  dispatch(pageLoading(true));
  try {
    const response = await localhost.get("/info");
    return response?.data;
  } catch (err) {
    dispatch(currentSessionInfo(null));
    return null;
  } finally {
    dispatch(pageLoading(false));
  }
};

export const getSessionInfo =
  (navigate, { skipPendingRedirect = false } = {}) =>
  async (dispatch) => {
    if (
      window.location.hash === "#/logged-out" ||
      window.location.hash === "#/saml-success" ||
      window.location.hash.startsWith("#/exception")
    ) {
      dispatch(setIsInitialized(true));
      return;
    }

    const data = await dispatch(fetchSessionInfo());
    if (data) {
      handleSessionInfo(dispatch, navigate, data, { skipPendingRedirect });
    }
    dispatch(setIsInitialized(true));
  };

export const appendInput = (form, name, value) => {
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = value;
  form.appendChild(input);
};

export const updateSessionAndLogin = (email, password) => async (dispatch) => {
  await localhost
    .get("/info")
    ?.then(async (response) => {
      const { data } = response;
      const form = document.createElement("form");
      form.method = "POST";
      form.action = "/login/processLogin";
      form.style.display = "none";
      appendInput(form, "username", email);
      appendInput(form, "password", password);
      appendInput(form, data?.csrf?.parameter, data?.csrf?.value);
      document.body.appendChild(form);
      form.submit();
    })
    .catch(() => {
      dispatch(showErrorModal({ message: "Cannot login at this time" }));
    });
};

export const logout = () => async (_, getState) => {
  const csrf = selectCsrf(getState());
  const username = selectUsername(getState());

  const { parameter, value } = csrf || {};

  const form = document.createElement("form");
  form.method = "POST";
  form.action = "/logout";
  appendInput(form, parameter, value);

  if (username) {
    appendInput(form, "username", username);
  }

  form.style.display = "none";
  document.body.appendChild(form);

  // Delete the local-storage of tracked sidemenu, collapsible groups.
  localStorage.removeItem(LS_SIDEMENU_GROUPS_CACHE);
  form.submit();
};
